import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Body, Stat, IconType,
} from '@routific/hive';
import DarkTooltipTable from '../../shared/DarkTooltipTable';
import { MiniRouteContainerType } from './MiniRouteInfo';
import { CAPACITY_LABEL_FORMAT } from '../../../constants/routeLabels';
import { GaugeSizeTypes } from './WorkingTimeInfo';
import TooltipBody from '../../shared/TooltipBody';

interface Props {
  totalLoad: number;
  overcapacity?: boolean;
  capacity?: number | null;
  percentLoad?: number;
  large?: boolean;
  type?: MiniRouteContainerType;
  remainingCapacity?: number;
  gaugeSize?: GaugeSizeTypes;
  loading?: boolean;
  disabled?: boolean;
}

const CapacityInfoItem = styled.div<{ large?: boolean, type?: Props['type'] }>`
  min-width: 0;
  max-width: ${({ large }) => (large ? '144px' : '100%')};
  display: flex;
  align-items: center;
`;

const CapacityInfo: React.FC<Props> = ({
  totalLoad, capacity, overcapacity,
  percentLoad, large, type,
  gaugeSize, loading = false,
  disabled,
}) => {
  const { t } = useTranslation('components');

  const getCapacityString = () => (loading ? '--' : t('constraintUtilization.capacity.label.consumption.text', { count: totalLoad }));

  const getTooltipContent = () => {
    return (
      <div data-cy="capacity-info-tooltip">
        <TooltipBody text={t('constraintUtilization.common.label.title')} />
        {!capacity ? (
          <Body type="tertiary" style={{ fontSize: 12 }}>{t('constraintUtilization.common.label.noLimitSet')}</Body>
        ) : (
          <DarkTooltipTable
            used={t(CAPACITY_LABEL_FORMAT, { count: totalLoad })}
            total={t(CAPACITY_LABEL_FORMAT, { count: capacity })}
            overCap={overcapacity}
          />
        )}
      </div>
    );
  };

  return (
    <CapacityInfoItem
      className="item"
      data-cy={type && `${type}-route-utilization-capacity`}
      data-testid={type && `${type}-route-utilization-capacity`}
      large={large}
    >
      <Stat
        percent={percentLoad || 0}
        constraintViolated={overcapacity}
        label={{ value: getCapacityString(), capacity: capacity ? `/ ${t('sidebar.units', { count: capacity })}` : undefined }}
        tooltipContent={getTooltipContent()}
        size={gaugeSize}
        icon={IconType.Load}
        disabled={disabled}
      />
    </CapacityInfoItem>
  );
};

export default CapacityInfo;
